/**
 * Pure helpers for the candidate-card match presentation in TalkToDavid.
 *
 * The backend's `match_score` is the matches-vectors weighted blend (Vector×10 +
 * Qwen÷10 + SoftScore) — an unbounded raw float, NOT a percentage. We don't
 * surface the number on the card. Instead we show:
 *   1. A rank-based pill badge ("Top match" / "Good match" / "Also consider").
 *   2. An optional intra-shortlist comparison bar, scaled relative to the
 *      strongest score in the same response. Bar only renders when the
 *      shortlist has at least two positive scores — a single-candidate result
 *      gives no comparison signal, and we'd rather show nothing than a 100%
 *      bar that overstates absolute quality.
 *
 * Backend ordering is best-first and is the source of truth for the badge.
 * We never re-sort here — see the spec note in the calling renderer.
 */

export type MatchBadge = 'Top match' | 'Good match' | 'Also consider';

export interface CandidateMatchInput {
  match_score?: number | null;
}

/**
 * Returns the rank-based badge for a candidate at zero-based index `rank`.
 *
 * - rank 0          → "Top match"
 * - rank 1, 2       → "Good match"
 * - rank 3 and up   → "Also consider"
 */
export function badgeForRank(rank: number): MatchBadge {
  if (rank <= 0) return 'Top match';
  if (rank <= 2) return 'Good match';
  return 'Also consider';
}

/**
 * Whether to render comparison bars for this shortlist.
 *
 * Bars are only meaningful when there's something to compare against — so we
 * require ≥2 candidates with positive numeric `match_score`. Missing / zero /
 * negative scores don't count toward the threshold.
 */
export function shouldShowBars(candidates: CandidateMatchInput[] | undefined | null): boolean {
  if (!candidates || candidates.length < 2) return false;
  const positives = candidates.filter(
    (c) => typeof c.match_score === 'number' && Number.isFinite(c.match_score) && c.match_score > 0,
  );
  return positives.length >= 2;
}

/**
 * Bar width (0–100) for one candidate, relative to the strongest score in the
 * same shortlist. Returns `null` when no bar should be drawn for this card —
 * either the score is missing/zero/non-positive, or `shouldShowBars` is false
 * for the shortlist as a whole.
 *
 * The width is intentionally NOT floored — a candidate scoring 1/100th of the
 * leader gets a 1-wide bar. We don't want to inflate weak matches into
 * "looks competitive at a glance" territory.
 */
export function barWidthPercent(
  candidate: CandidateMatchInput,
  shortlist: CandidateMatchInput[] | undefined | null,
): number | null {
  if (!shouldShowBars(shortlist)) return null;
  const score = candidate.match_score;
  if (typeof score !== 'number' || !Number.isFinite(score) || score <= 0) return null;

  const max = (shortlist as CandidateMatchInput[])
    .map((c) => (typeof c.match_score === 'number' && Number.isFinite(c.match_score) ? c.match_score : 0))
    .reduce((a, b) => (b > a ? b : a), 0);
  if (max <= 0) return null;

  const pct = (score / max) * 100;
  if (pct < 0) return 0;
  if (pct > 100) return 100;
  return pct;
}
